import { useOutletContext } from "react-router";

import { AlertTriangleIcon, CalendarIcon, FolderKanbanIcon, HashIcon, InfoIcon, PencilIcon, SettingsIcon, TagIcon, Trash2Icon, TypeIcon, UserIcon } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

import DeleteProjectForm from "@/features/projects/components/DeleteProjectForm";
import UpdateProjectForm from "@/features/projects/components/UpdateProjectForm";
import { PROJECT_STATUS_CONFIG } from "@/features/projects/config";
import { Project } from "@/features/projects/types";
import { PERMISSIONS } from "@/shared/lib/constants/permissions";

export default function ProjectSettings() {
  const { project, permissions } = useOutletContext() as { project: Project; permissions: string[] };

  const canUpdateProject = permissions.includes(PERMISSIONS.PROJECT_UPDATE);
  const canDeleteProject = permissions.includes(PERMISSIONS.PROJECT_DELETE);

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-1">
        <h2 className="font-heading text-lg font-semibold tracking-normal uppercase">Settings</h2>
        <p className="text-sm text-muted-foreground">Manage your project configuration and preferences.</p>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        <div className="flex flex-col gap-1.5">
          <h3 className="text-sm font-semibold">General</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">Update the project name, description and status. Changes are visible to all members immediately.</p>
        </div>
        <div className="flex flex-col gap-4">
          <UpdateProjectForm project={project} disabled={!canUpdateProject} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        <div className="flex flex-col gap-1.5">
          <h3 className="text-sm font-semibold">Project details</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">Read-only information about this project. Contact a workspace admin to change ownership or visibility.</p>
        </div>
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <InfoIcon className="size-4 text-primary" />
                <CardTitle>Project info</CardTitle>
              </div>
              <CardDescription>Read-only metadata about this project.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ul className="flex flex-col">
                <li>
                  <div className="flex items-center justify-between gap-4 px-6 py-3">
                    <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                      <HashIcon className="size-3.5 shrink-0" />
                      Project ID
                    </div>
                    <span className="text-xs font-medium text-right">
                      <code className="font-mono text-[11px] bg-muted px-1.5 py-0.5">{project.id}</code>
                    </span>
                  </div>
                </li>
                {!canUpdateProject && (
                  <>
                    <li>
                      <div className="flex items-center justify-between gap-4 px-6 py-3">
                        <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                          <TypeIcon className="size-3.5 shrink-0" />
                          Name
                        </div>
                        <span className="text-xs font-medium text-right">{project.name}</span>
                      </div>
                    </li>
                    <li>
                      <div className="flex items-start justify-between gap-4 px-6 py-3">
                        <div className="flex items-center gap-2.5 text-xs text-muted-foreground shrink-0">
                          <PencilIcon className="size-3.5 shrink-0" />
                          Description
                        </div>
                        <span className="text-xs font-medium text-right">{project.description ?? <span className="text-muted-foreground italic">No description</span>}</span>
                      </div>
                    </li>
                  </>
                )}
                <li>
                  <div className="flex items-center justify-between gap-4 px-6 py-3">
                    <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                      <UserIcon className="size-3.5 shrink-0" />
                      Owner
                    </div>
                    <span className="text-xs font-medium text-right">{[project.createdBy.firstName, project.createdBy.lastName].join(" ")}</span>
                  </div>
                </li>
                <li>
                  <div className="flex items-center justify-between gap-4 px-6 py-3">
                    <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                      <CalendarIcon className="size-3.5 shrink-0" />
                      Created
                    </div>
                    <span className="text-xs font-medium text-right">{project.createdAt.toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span>
                  </div>
                </li>
                <li>
                  <div className="flex items-center justify-between gap-4 px-6 py-3">
                    <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                      <FolderKanbanIcon className="size-3.5 shrink-0" />
                      Visibility
                    </div>
                    <span className="text-xs font-medium text-right">{project.visibility}</span>
                  </div>
                </li>
                <li>
                  <div className="flex items-center justify-between gap-4 px-6 py-3">
                    <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                      <TagIcon className="size-3.5 shrink-0" />
                      Status
                    </div>
                    <span className="text-xs font-medium text-right">
                      <Badge variant="secondary" className={PROJECT_STATUS_CONFIG[project.status]?.className ?? "text-muted-foreground"}>
                        {project.status}
                      </Badge>
                    </span>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        <div className="flex flex-col gap-1.5">
          <h3 className="text-sm font-semibold">Danger zone</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">Actions here are destructive and permanent. Proceed with caution - deleted projects cannot be restored.</p>
        </div>
        <div className="flex flex-col gap-4">
          <Card className="border-destructive/40">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangleIcon className="size-4 text-destructive" />
                <CardTitle className="text-destructive">Danger zone</CardTitle>
              </div>
              <CardDescription>Irreversible actions that permanently affect this project.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-6">
              <Alert variant="destructive">
                <AlertTriangleIcon />
                <AlertTitle>Deleting a project is permanent</AlertTitle>
                <AlertDescription>All tickets, member associations and activity history will be immediately and irrecoverably deleted. This action cannot be undone.</AlertDescription>
              </Alert>
              <div className="flex flex-col gap-4 border border-destructive/30 p-4">
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-semibold">Delete this project</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Once you delete <span className="font-semibold text-foreground">{project.name}</span>, there is no going back. All{" "}
                    <span className="font-semibold text-foreground">
                      {project.totalTicketCount} {project.totalTicketCount == 1 ? "ticket" : "tickets"}
                    </span>{" "}
                    and{" "}
                    <span className="font-semibold text-foreground">
                      {project.totalMemberCount} {project.totalMemberCount == 1 ? "member" : "members"}
                    </span>{" "}
                    will be removed permanently.
                  </p>
                </div>
                <DeleteProjectForm project={project} disabled={!canDeleteProject} />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
